// Pure relative-date presets (no UI / no app imports) so they can be unit-tested.
// "Today" is resolved in the given IANA timezone; week presets honour firstDay (0 Sun / 1 Mon / 6 Sat).
export const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7", label: "Last 7 days" },
  { value: "last_30", label: "Last 30 days" },
  { value: "last_90", label: "Last 90 days" },
  { value: "last_n", label: "Last N days…" },
  { value: "this_week", label: "This week" },
  { value: "last_week", label: "Last week" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "this_quarter", label: "This quarter" },
  { value: "this_year", label: "This year" },
  { value: "last_year", label: "Last year" },
  { value: "this_fiscal_year", label: "This fiscal year" },
  { value: "last_fiscal_year", label: "Last fiscal year" },
  { value: "custom", label: "Custom range" },
];

const DAY = 24 * 3600 * 1000;
export const isoDay = (d) => d.toISOString().slice(0, 10);

export function todayIn(timezone, now = new Date()) {
  if (timezone) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
      const g = (t) => Number(parts.find((p) => p.type === t)?.value);
      return new Date(Date.UTC(g("year"), g("month") - 1, g("day")));
    } catch { /* unknown zone → UTC */ }
  }
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

export function resolvePreset(code, { fiscalStart = 1, timezone = "", firstDay = 1, now = new Date() } = {}) {
  const today = todayIn(timezone, now);
  const y = today.getUTCFullYear(), m = today.getUTCMonth();
  const fs = Math.min(12, Math.max(1, Number(fiscalStart) || 1)) - 1;
  const fd = [0, 1, 6].includes(Number(firstDay)) ? Number(firstDay) : 1;
  const weekStart = (d) => new Date(d - ((d.getUTCDay() - fd + 7) % 7) * DAY);
  switch (code) {
    case "today": return [isoDay(today), isoDay(today)];
    case "yesterday": { const yd = new Date(today - DAY); return [isoDay(yd), isoDay(yd)]; }
    case "last_7": return [isoDay(new Date(today - 7 * DAY)), isoDay(today)];
    case "last_30": return [isoDay(new Date(today - 30 * DAY)), isoDay(today)];
    case "last_90": return [isoDay(new Date(today - 90 * DAY)), isoDay(today)];
    case "this_week": return [isoDay(weekStart(today)), isoDay(today)];
    case "last_week": { const ws = weekStart(today); return [isoDay(new Date(ws - 7 * DAY)), isoDay(new Date(ws - DAY))]; }
    case "this_month": return [isoDay(new Date(Date.UTC(y, m, 1))), isoDay(today)];
    case "last_month": return [isoDay(new Date(Date.UTC(y, m - 1, 1))), isoDay(new Date(Date.UTC(y, m, 0)))];
    case "this_quarter": return [isoDay(new Date(Date.UTC(y, Math.floor(m / 3) * 3, 1))), isoDay(today)];
    case "this_year": return [isoDay(new Date(Date.UTC(y, 0, 1))), isoDay(today)];
    case "last_year": return [isoDay(new Date(Date.UTC(y - 1, 0, 1))), isoDay(new Date(Date.UTC(y - 1, 11, 31)))];
    case "this_fiscal_year": {
      const start = m >= fs ? new Date(Date.UTC(y, fs, 1)) : new Date(Date.UTC(y - 1, fs, 1));
      return [isoDay(start), isoDay(today)];
    }
    case "last_fiscal_year": {
      const thisStart = m >= fs ? new Date(Date.UTC(y, fs, 1)) : new Date(Date.UTC(y - 1, fs, 1));
      const prevStart = new Date(Date.UTC(thisStart.getUTCFullYear() - 1, fs, 1));
      return [isoDay(prevStart), isoDay(new Date(thisStart - DAY))];
    }
    default: return null;
  }
}
